import React, { Suspense } from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";

import SiteLayout from "./layouts/SiteLayout";
import MainLayout from "./layouts/MainLayout";

import GuestGuard from "./guards/GuestGuard";
import UserGuard from "./guards/UserGuard";

import LoadingScreen from "./components/LoadingScreen";
import Login from "./views/auth/Login";
import Register from "./views/auth/Register";
import ForgetPass from "./views/auth/ForgetPass";
import VerifyAccountPassword from "./views/auth/ForgetPass/VerifyAccountPassword";
import LoginWithOtp from "./views/auth/Login/LoginWithOtp";

import CreatorList from "./views/user/CreatorList/CreatorList";
import Chat from "./views/user/Chat/ChatPage";
// Creator-side views from before the fan app and creator dashboard were split
// into separate repos. They now live in syntimacy-creators, and /user/dashboard
// — their only entry point — is gone, so they are no longer routed here. The
// files are left on disk for reference rather than deleted.
import VideoCall from "./views/user/VideoCall/VideoCall";
import MyAccount from "./views/user/MyProfile/MyProfile";
import Plans from "./views/user/plans/plans";
import Payment from "./views/user/payment/payment";
import PaymentSuccess from "./views/user/payment/paymentSuccess";
import PlansCancel from "./views/user/plans/cancelPlan";
import MagicLink from "./views/user/magicLink/magicLink";
import AddToHome from "./views/user/AddToHome/addToHome";
import BillingHistory from "./views/user/billingHistory/billingHistory";
import Help from "./views/user/help/help";
import TermsOfService from "./views/user/terms/TermsOfService";
import PrivacyPolicy from "./views/user/privacy/PrivacyPolicy";
import DeleteAccount from "./views/user/deleteAccount/deleteAccount";
import Settings from "./views/user/settings/settings";
import Notification from "./views/user/Notofication/notification";
import PrivacyData from "./views/user/DataProtect/privacyData";
import SubscriptionFaild from "./views/user/payment/SubscriptionFailed";
import AccountSetting from "./views/user/accountSetting/accountSettings";
import CreatorProfile from "./views/user/CreatorProfile/CreatorProfile";

import ChatLanding from "./views/user/Chat/ChatLandingPage";
import Page404 from "./views/errors/Page404";
import SSOCallback from "./views/user/ssoCallback/ssoCallBack";
import CreatorNotFound from "./views/errors/CreatorNotFound";
import CommunityStandards from "./views/user/community/CommunityGuidelines";
import AntiTraffickingPolicy from "./views/user/privacy/Antitraffickingpolicy";
import ComplaintsPolicy from "./views/user/privacy/ComplaintsPolicy";
import AgeGate from "./components/Agegate/Agegate";
import AccessDenied from "./views/errors/AccessDenied";


// ==================
// Layout Wrappers
// ==================

const GuestLayout = () => (
  <AgeGate>
  <GuestGuard>
    <SiteLayout>
      <Outlet />
    </SiteLayout>
  </GuestGuard>
  </AgeGate>
);

const UserLayout = () => (
  <AgeGate>
  <UserGuard>
    <MainLayout>
      <Outlet />
    </MainLayout>
  </UserGuard>
  </AgeGate>
);


// ==================
// Main Routes
// ==================

export default function RenderRoutes() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>

        {/* ================= PUBLIC ROUTES ================= */}
        <Route element={<GuestLayout />}>
          <Route path="/" element={<Navigate to="/login" />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forget-pass" element={<ForgetPass />} />
          <Route path="/otp-verification" element={<VerifyAccountPassword />} />
          <Route path="/login-with-otp" element={<LoginWithOtp />} />
        </Route>
        <Route path="/sso-callback" element={<SSOCallback />} />
        {/* Creator magic link. Own 30-day pass so confirming age on /login
            does not skip this gate, and the splash stays unmounted until they pass. */}
        <Route path="/:username" element={<AgeGate storageKey="tlk_magic_age_ok_until"><ChatLanding /></AgeGate>} />
        {/* ================= USER ROUTES ================= */}
        <Route element={<UserLayout />}>
          <Route path="/user" element={<Navigate to="/user/creator-list" />} />

          <Route path="/user/creator-list" element={<CreatorList />} />
          <Route path="/user/chat" element={<Chat />} />
          <Route path="/user/creator-profile" element={<CreatorProfile />} />
          <Route path="/user/video-call" element={<VideoCall />} />
          <Route path="/user/my-profile" element={<MyAccount />} />
          <Route path="/user/subscription-plans" element={<Plans />} />
          <Route path="/user/payment" element={<Payment />} />
          <Route path="/user/success" element={<PaymentSuccess />} />
          <Route path="/user/cancel-plan" element={<PlansCancel />} />
          <Route path="/user/magic-link" element={<MagicLink />} />
          <Route path="/user/add-to-home" element={<AddToHome />} />
          <Route path="/user/billing-history" element={<BillingHistory />} />
          <Route path="/user/delete-account" element={<DeleteAccount />} />
          <Route path="/user/settings" element={<Settings />} />
          <Route path="/user/notification" element={<Notification />} />
          <Route path="/user/data-protect" element={<PrivacyData />} />
          <Route path="/user/failed" element={<SubscriptionFaild />} />
          <Route path="/user/manage-account" element={<AccountSetting />} />

        </Route>


        {/* ================= 404 ================= */}
        <Route path="/404" element={<Page404 />} />
        <Route path="*" element={<Navigate to="/404" />} />
        <Route path="/creator-not-found/:username" element={<CreatorNotFound />} />
        <Route path="/access-denied" element={<AccessDenied />} />
        <Route path="/user/terms" element={<TermsOfService />} />
        <Route path="/user/privacy" element={<PrivacyPolicy />} />
        <Route path="/community-guidelines" element={<CommunityStandards />} />
        <Route path="/user/help" element={<Help />} />
        <Route path="/anti-trafficking-policy" element={<AntiTraffickingPolicy />} />
        <Route path="/complaints-policy" element={<ComplaintsPolicy />} />
      </Routes>
    </Suspense>
  );
}